/** Permanent materials; task state never changes the coat or eyes. */
export const danteMaterials = {
  ivory: "#E6DDC8",
  russet: "#A6673F",
  saddle: "#383A32",
  eyes: "#4E3B29",
  ink: "#252925",
  muzzle: "#C4AF91",
  tab: "#A65038",
} as const;

export const dantePalettes = {
  field: {
    background: "#EEE7D7",
    annotation: "#292B28",
    harness: "#344B59",
    harnessHighlight: "#627480",
    leather: "#876347",
    brass: "#A58E57",
    map: "#A7A08E",
    observing: "#536C75",
    uncertain: "#826C3F",
    complete: "#526C55",
    waiting: "#9B503B",
    error: "#8B4238",
  },
  watch: {
    background: "#18232B",
    annotation: "#D7DCD5",
    harness: "#293F49",
    harnessHighlight: "#657C85",
    leather: "#695440",
    brass: "#968466",
    map: "#71838B",
    observing: "#9FB7BE",
    uncertain: "#C7B17A",
    complete: "#9EB399",
    waiting: "#D99372",
    error: "#D68E83",
  },
  dispatch: {
    background: "#F1EADF",
    annotation: "#303832",
    harness: "#37463F",
    harnessHighlight: "#6A786D",
    leather: "#725A45",
    brass: "#8A805D",
    map: "#AAA796",
    observing: "#526B63",
    uncertain: "#7E6B45",
    complete: "#516A58",
    waiting: "#9B503B",
    error: "#884439",
  },
} as const;

export type DantePaletteColors = { -readonly [K in keyof typeof dantePalettes.field]: string };

/** Composite equipment and environment in linear RGB, then encode the result in sRGB. */
export function blendDantePalette(
  from: DantePaletteColors,
  to: DantePaletteColors,
  progress: number,
): DantePaletteColors {
  const t = Math.max(0, Math.min(1, progress));
  const linear = (value: number) =>
    value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  const srgb = (value: number) =>
    value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
  const result = {} as DantePaletteColors;
  for (const key of Object.keys(from) as (keyof DantePaletteColors)[]) {
    result[key] = `#${[1, 3, 5]
      .map((offset) => {
        const a = linear(Number.parseInt(from[key].slice(offset, offset + 2), 16) / 255);
        const b = linear(Number.parseInt(to[key].slice(offset, offset + 2), 16) / 255);
        return Math.round(srgb(a + (b - a) * t) * 255)
          .toString(16)
          .padStart(2, "0");
      })
      .join("")}`;
  }
  return result;
}

export function renderDanteTokensCss(): string {
  const variables = (values: Record<string, string>) =>
    Object.entries(values)
      .map(
        ([name, value]) =>
          `  --dante-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}: ${value.toLowerCase()};`,
      )
      .join("\n");
  return [
    `:root {\n${variables(danteMaterials)}\n}`,
    ...Object.entries(dantePalettes).map(([name, palette]) => {
      const inherited =
        name === "watch"
          ? ':root,\n[data-theme="dark"],\n'
          : name === "field"
            ? '[data-theme="light"],\n'
            : "";
      return `${inherited}[data-dante-palette="${name}"] {\n${variables(palette)}\n}`;
    }),
  ].join("\n\n");
}

export const APPEARANCE_PREFERENCES = ["system", "light", "dark"] as const;

export type AppearancePreference = (typeof APPEARANCE_PREFERENCES)[number];

export type ResolvedAppearance = "light" | "dark";

export const UI_APPEARANCE_STORAGE_KEY = "dante.uiAppearance";

/**
 * Semantic palette shared by web, Electron, and Expo. Names follow the shadcn
 * convention so the same slot means the same thing on every surface: a `border`
 * is always a border and never a fill.
 */
export type ColorTokens = {
  background: string;
  /** Modals: a step lighter than the page so they read as raised above it. */
  dialog: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  /** Fill and hairline where native Liquid Glass is unavailable (Android, iOS before 26). */
  glassFill: string;
  glassStroke: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  chatUser: string;
  /** Contrasting text on the neutral user message surface. */
  chatUserForeground: string;
  muted: string;
  mutedForeground: string;
  /** Tertiary text: placeholders, list markers, quote bars, timestamps. */
  faintForeground: string;
  accent: string;
  accentForeground: string;
  /** Destructive text and outlines. */
  destructive: string;
  /** Solid destructive fill (Hang up, a filled Delete); its text is `destructiveForeground`. */
  destructiveFill: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  link: string;
  /** Backing behind a link's site icon. White in both themes so dark logos stay visible on any bubble. */
  faviconPlate: string;
  success: string;
  warning: string;
  statusWorking: string;
  /** Unread indicators use neutral high-contrast ink. */
  unread: string;
  unreadForeground: string;
  /** Working indicators use neutral ink, distinct from completed results. */
  workingDot: string;
  overlay: string;
  scrollbar: string;
  scrollbarHover: string;
};

export const darkTokens = {
  background: "#17191C",
  dialog: "#202226",
  foreground: "#E8E9E6",
  card: "#202226",
  cardForeground: "#E8E9E6",
  popover: "#25272B",
  popoverForeground: "#E8E9E6",
  glassFill: "#202226",
  glassStroke: "#E8E9E626",
  primary: "#E8E9E6",
  primaryForeground: "#17191C",
  secondary: "#2B2D31",
  secondaryForeground: "#E8E9E6",
  chatUser: "#303338",
  chatUserForeground: "#F1F2EF",
  muted: "#222428",
  mutedForeground: "#B5B8BD",
  faintForeground: "#92969E",
  accent: "#FFFFFF0D",
  accentForeground: "#E8E9E6",
  destructive: "#F0A49B",
  destructiveFill: "#913F36",
  destructiveForeground: "#FFFFFF",
  border: "#373A40",
  input: "#50545C",
  ring: "#B7BBC2",
  sidebar: "#121417",
  sidebarForeground: "#E8E9E6",
  sidebarBorder: "#303339",
  sidebarAccent: "#292C31",
  sidebarAccentForeground: "#E8E9E6",
  link: "#E8E9E6",
  faviconPlate: "#FFFFFF",
  success: "#A3C7A5",
  warning: "#DDC088",
  statusWorking: "#B7BBC2",
  unread: "#B7BBC2",
  unreadForeground: "#17191C",
  workingDot: "#B7BBC2",
  overlay: "rgba(0, 0, 0, 0.48)",
  scrollbar: "#50545C",
  scrollbarHover: "#92969E",
} as const satisfies ColorTokens;

export const lightTokens = {
  background: "#F2F1ED",
  dialog: "#FCFCFA",
  foreground: "#232529",
  card: "#FFFFFF",
  cardForeground: "#232529",
  popover: "#FFFFFF",
  popoverForeground: "#232529",
  glassFill: "#FFFFFF",
  glassStroke: "#23252926",
  primary: "#232529",
  primaryForeground: "#FCFCFA",
  secondary: "#E8E7E3",
  secondaryForeground: "#232529",
  chatUser: "#E2E3DF",
  chatUserForeground: "#232529",
  muted: "#EAE9E5",
  mutedForeground: "#53565D",
  faintForeground: "#74777D",
  accent: "#23252909",
  accentForeground: "#232529",
  destructive: "#913F36",
  destructiveFill: "#913F36",
  destructiveForeground: "#FFFFFF",
  border: "#D4D4CF",
  input: "#A3A5A3",
  ring: "#50545A",
  sidebar: "#E8E8E3",
  sidebarForeground: "#232529",
  sidebarBorder: "#CDCEC8",
  sidebarAccent: "#FCFCFA",
  sidebarAccentForeground: "#232529",
  link: "#232529",
  faviconPlate: "#FFFFFF",
  success: "#3C6845",
  warning: "#795B24",
  statusWorking: "#50545A",
  unread: "#50545A",
  unreadForeground: "#FCFCFA",
  workingDot: "#50545A",
  overlay: "rgba(0, 0, 0, 0.24)",
  scrollbar: "#A3A5A3",
  scrollbarHover: "#74777D",
} as const satisfies ColorTokens;

/** Dark palette. Prefer `tokensForAppearance` when theme-aware. */
export const tokens = darkTokens;

/** Precise panel corners, shared with native custom surfaces. */
export const interfaceGeometry = { controlRadius: 6, panelRadius: 10, bubbleRadius: 10 } as const;
export const RADIUS = `${interfaceGeometry.controlRadius / 16}rem`;

export const interfaceFontFamily = "IBM Plex Sans";

export const fontWeight = { regular: "400", medium: "500", semibold: "600" } as const;

/**
 * Native custom text uses bundled IBM Plex Sans, in points: body text is 17/22. Titles use the
 * font's own line spacing, written out so they don't inherit a smaller body line height.
 */
export const typeScale = {
  title: {
    fontFamily: interfaceFontFamily,
    fontSize: 22,
    lineHeight: 26,
    fontWeight: fontWeight.semibold,
  },
  headline: {
    fontFamily: interfaceFontFamily,
    fontSize: 19,
    lineHeight: 23,
    fontWeight: fontWeight.semibold,
  },
  cardTitle: {
    fontFamily: interfaceFontFamily,
    fontSize: 17,
    lineHeight: 22,
    fontWeight: fontWeight.medium,
  },
  body: { fontFamily: interfaceFontFamily, fontSize: 17, lineHeight: 22 },
  secondary: { fontFamily: interfaceFontFamily, fontSize: 15, lineHeight: 19 },
  caption: { fontFamily: interfaceFontFamily, fontSize: 14, lineHeight: 18 },
  footnote: { fontFamily: interfaceFontFamily, fontSize: 12, lineHeight: 16 },
} as const;

/**
 * Text grows with the iOS text size until body text reaches 23pt, the xxxLarge size. Larger
 * accessibility sizes keep that size, so message bubbles, rows and cards keep their layout.
 */
export const MAX_FONT_SCALE = 23 / typeScale.body.fontSize;

export const botColors = [
  "#3EC5A8",
  "#F5A03C",
  "#6A6BF5",
  "#9B5CF6",
  "#3B82F6",
  "#F2622A",
  "#D9508A",
] as const;

export function isAppearancePreference(
  value: string | null | undefined,
): value is AppearancePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function normalizeAppearancePreference(
  raw: string | null | undefined,
): AppearancePreference {
  return isAppearancePreference(raw) ? raw : "system";
}

export type ResolveAppearancePreferenceOptions = {
  stored?: string | null;
  storage?: Pick<Storage, "getItem"> | null;
};

function getLocalStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function resolveAppearancePreference(
  options: ResolveAppearancePreferenceOptions = {},
): AppearancePreference {
  const stored =
    options.stored !== undefined
      ? options.stored
      : readStoredAppearance(options.storage ?? getLocalStorage());
  return normalizeAppearancePreference(stored);
}

export function persistAppearancePreference(
  preference: AppearancePreference,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(UI_APPEARANCE_STORAGE_KEY, preference);
  } catch {
    // Ignore quota / private-mode failures; in-memory preference still applies.
  }
}

export function resolveAppearance(
  preference: AppearancePreference,
  system: ResolvedAppearance = "dark",
): ResolvedAppearance {
  if (preference === "system") return system;
  return preference;
}

export function tokensForAppearance(appearance: ResolvedAppearance): ColorTokens {
  return appearance === "light" ? lightTokens : darkTokens;
}

function readStoredAppearance(storage: Pick<Storage, "getItem"> | null | undefined): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(UI_APPEARANCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** `cardForeground` -> `--card-foreground` */
export function cssVariableName(token: keyof ColorTokens): string {
  return `--${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

function renderBlock(selector: string, colorScheme: ResolvedAppearance, palette: ColorTokens) {
  const lines = (Object.keys(palette) as (keyof ColorTokens)[]).map(
    (token) => `  ${cssVariableName(token)}: ${palette[token].toLowerCase()};`,
  );
  return `${selector} {\n  color-scheme: ${colorScheme};\n${lines.join("\n")}\n  --radius: ${RADIUS};\n}`;
}

/** The CSS in `tokens.css`. Generated from the TS palette so both stay in sync. */
export function renderTokensCss(): string {
  return `${[
    "/* Generated by `pnpm --filter @dante/ui-tokens generate`. Edit src/index.ts instead. */",
    renderBlock(':root,\n[data-theme="dark"]', "dark", darkTokens),
    renderBlock('[data-theme="light"]', "light", lightTokens),
    renderDanteTokensCss(),
  ].join("\n\n")}\n`;
}
